import React, { Fragment, useMemo, useState } from "react";

const getInitial = (username = "?") => username.trim().charAt(0).toUpperCase() || "?";

const MAX_PREVIEW_CHARS = 420;

const extractText = (source) => {
    if (!source) return "";

    if (typeof source === "string") {
        return source;
    }

    if (Array.isArray(source)) {
        return source
            .map((item) => extractText(item))
            .filter(Boolean)
            .join("\n")
            .replace(/\n{2,}/g, "\n");
    }

    if (typeof source === "object") {
        if (typeof source.text === "string") {
            return source.text;
        }

        if (Array.isArray(source.children)) {
            return source.children.map((child) => extractText(child)).join("");
        }

        if (Array.isArray(source.ops)) {
            return source.ops
                .map((op) => (typeof op.insert === "string" ? op.insert : ""))
                .join("");
        }

        const nestedKeys = ["content", "body", "text", "message", "value", "data"];
        for (const key of nestedKeys) {
            if (!source[key]) continue;
            const nestedText = extractText(source[key]);
            if (nestedText.trim()) {
                return nestedText;
            }
        }
    }

    return "";
};

const pickCommentContent = (comment) => {
    const candidates = [
        comment?.content,
        comment?.body,
        comment?.text,
        comment?.message,
    ];

    for (const candidate of candidates) {
        const extracted = extractText(candidate);
        if (extracted.trim()) {
            return extracted;
        }
    }

    const fallback = extractText(comment?.content);
    return fallback.trim() ? fallback : "";
};

const Comment = ({ comment }) => {
    const username = comment.author?.username || "旅人";
    const createdAt = comment.created_at || comment.createdAt;
    const content = useMemo(() => pickCommentContent(comment), [comment]);

    const formattedContent = useMemo(() => {
        if (typeof content !== "string" || content.length === 0) {
            return null;
        }
        return content.split(/\n/).map((segment, index, parts) => (
            <Fragment key={index}>
                {segment}
                {index < parts.length - 1 && <br />}
            </Fragment>
        ));
    }, [content]);

    const needsTruncation = useMemo(() => {
        return typeof content === "string" && content.length > MAX_PREVIEW_CHARS;
    }, [content]);

    const [expanded, setExpanded] = useState(false);

    return (
        <div className="comment">
            <div className="comment-meta">
                <div className="comment-avatar" aria-hidden="true">
                    {getInitial(username)}
                </div>
                <div className="comment-meta-text">
                    <span className="comment-author">{username}</span>
                    {createdAt && (
                        <span className="comment-timestamp">
                            {new Date(createdAt).toLocaleString()}
                        </span>
                    )}
                </div>
            </div>
            <p className={`comment-body ${expanded ? "is-expanded" : "is-collapsed"}`}>
                {formattedContent}
            </p>
            {needsTruncation && (
                <button
                    type="button"
                    className="comment-toggle"
                    onClick={() => setExpanded((prev) => !prev)}
                    aria-expanded={expanded}
                >
                    {expanded ? "收起" : "展開"}
                </button>
            )}
        </div>
    );
};

export default Comment;
